<!--
  Several lines over time (running totals per store, category, card…). Hover or drag to read
  every line at that point; a legend names each one. Values are in micro.
-->
<script lang="ts">
  import { compactMicro, formatMicro } from "../../lib/format.ts";

  export interface LineSeries {
    name: string;
    color: string;
    values: number[];
  }

  let {
    labels,
    series,
    height = 180,
    title,
  }: { labels: string[]; series: LineSeries[]; height?: number; title: string } = $props();

  let width = $state(600);
  let hover = $state<number | null>(null);

  const pad = { top: 12, right: 12, bottom: 24, left: 52 };
  const n = $derived(labels.length);
  const max = $derived(Math.max(1, ...series.flatMap((s) => s.values)));
  const nice = $derived.by(() => {
    const step = 10 ** Math.floor(Math.log10(max / 3 || 1));
    const s = [1, 2, 2.5, 5, 10].map((m) => m * step).find((x) => max / x <= 4) ?? step * 10;
    const hi = Math.ceil(max / s) * s;
    const ticks: number[] = [];
    for (let v = 0; v <= hi + s / 2; v += s) ticks.push(v);
    return { hi, ticks };
  });
  const iw = $derived(Math.max(10, width - pad.left - pad.right));
  const ih = $derived(height - pad.top - pad.bottom);
  const x = (i: number) => pad.left + (n <= 1 ? iw / 2 : (i / (n - 1)) * iw);
  const y = (v: number) => pad.top + ih - (v / (nice.hi || 1)) * ih;
  const path = (vals: number[]) => vals.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join("");
  const xTicks = $derived(n <= 1 ? [0] : [...new Set([0, Math.floor((n - 1) / 2), n - 1])]);

  function move(e: PointerEvent) {
    const r = (e.currentTarget as SVGElement).getBoundingClientRect();
    const i = Math.round(((e.clientX - r.left - pad.left) / iw) * (n - 1));
    hover = Math.max(0, Math.min(n - 1, i));
  }
  const at = $derived(hover ?? n - 1);
  const ranked = $derived([...series].sort((a, b) => (b.values[at] ?? 0) - (a.values[at] ?? 0)));
</script>

<figure class="chart" bind:clientWidth={width}>
  <figcaption class="sr-only">{title}</figcaption>
  {#if n && series.length}
    <svg role="img" aria-label={title} {width} {height} onpointermove={move} onpointerdown={move} onpointerleave={() => (hover = null)}>
      {#each nice.ticks as t, ti (ti)}
        <line class="grid" class:zero={t === 0} x1={pad.left} x2={width - pad.right} y1={y(t)} y2={y(t)} />
        <text class="ylab" x={pad.left - 8} y={y(t)} dy="0.32em">{compactMicro(t)}</text>
      {/each}
      {#each xTicks as i (i)}
        <text class="xlab" x={x(i)} y={height - 6} text-anchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}>{labels[i]}</text>
      {/each}
      {#each series as s (s.name)}
        <path d={path(s.values)} fill="none" stroke={s.color} stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />
      {/each}
      {#if hover !== null}
        <line class="cross" x1={x(hover)} x2={x(hover)} y1={pad.top} y2={pad.top + ih} />
        {#each series as s (s.name)}
          <circle cx={x(hover)} cy={y(s.values[hover] ?? 0)} r="4" fill={s.color} stroke="var(--surface)" stroke-width="2" />
        {/each}
      {/if}
    </svg>
    <div class="legend" aria-live="polite">
      <span class="when muted">{hover === null ? `By ${labels[n - 1]}` : labels[hover]}</span>
      {#each ranked as s (s.name)}
        <span class="key"><span class="sw" style:background={s.color}></span>{s.name} <span class="num">{formatMicro(s.values[at] ?? 0)}</span></span>
      {/each}
    </div>
  {:else}
    <p class="muted empty">Nothing in this range.</p>
  {/if}
</figure>

<style>
  .chart {
    margin: 0;
    min-width: 0;
  }

  svg {
    display: block;
    touch-action: pan-y;
    cursor: crosshair;
    overflow: visible;
  }

  .grid {
    stroke: var(--rule);
  }

  .grid.zero {
    stroke: var(--rule-strong);
  }

  .ylab,
  .xlab {
    fill: var(--muted);
    font-size: 11px;
  }

  .ylab {
    text-anchor: end;
  }

  .cross {
    stroke: var(--ink-2);
    stroke-dasharray: 3 3;
  }

  .legend {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    margin-top: 6px;
    font-size: var(--t-xs);
  }

  .when {
    flex-basis: 100%;
  }

  .key {
    display: inline-flex;
    gap: 5px;
    align-items: center;
  }

  .sw {
    width: 10px;
    height: 3px;
    border-radius: 2px;
  }

  .num {
    font-weight: 650;
    font-variant-numeric: tabular-nums;
  }

  .empty {
    margin: 8px 0;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }
</style>
